import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PublicLayout } from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Bem-vindo, ${u.name}!`);
      if (u.role === "master" || u.role === "admin") nav("/admin");
      else if (u.role === "professional") nav("/painel/profissional");
      else if (u.role === "clinic") nav("/painel/clinica");
      else nav("/app/associado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao entrar");
    } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <div className="max-w-md mx-auto px-6 py-16">
        <h1 className="text-3xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}}>Entrar</h1>
        <p className="text-sm text-slate-500 mt-1">Acesse sua conta GIRO+SAÚDE.</p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <Input data-testid="login-email" type="email" placeholder="E-mail" value={email} onChange={e=>setEmail(e.target.value)} required/>
          <Input data-testid="login-password" type="password" placeholder="Senha" value={password} onChange={e=>setPassword(e.target.value)} required/>
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-[#0D7C66] hover:bg-[#074E41]">{loading?"Entrando...":"Entrar"}</Button>
        </form>
        <div className="mt-6 text-sm text-slate-600 text-center">
          Não tem conta? <Link data-testid="login-to-signup" to="/cadastro" className="text-[#0D7C66] font-semibold">Criar conta</Link>
        </div>
      </div>
    </PublicLayout>
  );
}

const CITIES = ["Santos","Praia Grande","São Vicente","Guarujá","Cubatão","Bertioga","Mongaguá","Itanhaém","Peruíbe"];

export function Cadastro() {
  const [sp] = useSearchParams();
  const initialRole = sp.get("role") || "associate";
  const { register } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [f, setF] = useState({
    name:"", email:"", phone:"", cpf:"", birth_date:"", city:"",
    address:"", zip_code:"", password:"", role: initialRole,
  });
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const next = () => setStep(s => Math.min(s+1, 5));
  const back = () => setStep(s => Math.max(s-1, 1));

  const submit = async () => {
    if (!terms) { toast.error("Aceite os termos para continuar"); return; }
    setLoading(true);
    try {
      const u = await register(f);
      toast.success(`Conta criada! Bem-vindo, ${u.name}.`);
      if (u.role === "professional") nav("/painel/profissional");
      else if (u.role === "clinic") nav("/painel/clinica");
      else nav("/app/associado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <div className="max-w-lg mx-auto px-6 py-12">
        <div className="flex gap-1 mb-6">
          {[1,2,3,4,5].map(i=>(<div key={i} className={`flex-1 h-1.5 rounded-full ${i<=step?"bg-[#0D7C66]":"bg-slate-200"}`}/>))}
        </div>
        <div className="text-xs uppercase tracking-widest text-[#0D7C66] font-semibold">Etapa {step} de 5</div>
        <h1 className="text-3xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}}>Criar conta</h1>

        <div className="mt-6 space-y-3">
          {step===1 && (<>
            <div className="grid grid-cols-3 gap-2">
              {[["associate","Associado"],["professional","Profissional"],["clinic","Clínica"]].map(([v,l])=>(
                <button key={v} type="button" data-testid={`role-${v}`} onClick={()=>setF({...f,role:v})} className={`p-3 rounded-xl border text-sm font-medium ${f.role===v?"border-[#0D7C66] bg-[#E6F4F1] text-[#0D7C66]":"border-slate-200"}`}>{l}</button>
              ))}
            </div>
            <Input data-testid="cad-name" placeholder="Nome completo" value={f.name} onChange={e=>setF({...f,name:e.target.value})}/>
            <Input data-testid="cad-email" type="email" placeholder="E-mail" value={f.email} onChange={e=>setF({...f,email:e.target.value})}/>
            <Input data-testid="cad-phone" placeholder="Telefone/WhatsApp" value={f.phone} onChange={e=>setF({...f,phone:e.target.value})}/>
          </>)}
          {step===2 && (<>
            <Input data-testid="cad-cpf" placeholder="CPF" value={f.cpf} onChange={e=>setF({...f,cpf:e.target.value})}/>
            <Input data-testid="cad-birth" placeholder="Data de nascimento (DD/MM/AAAA)" value={f.birth_date} onChange={e=>setF({...f,birth_date:e.target.value})}/>
            <Input data-testid="cad-zip" placeholder="CEP" value={f.zip_code} onChange={e=>setF({...f,zip_code:e.target.value})}/>
            <select data-testid="cad-city" value={f.city} onChange={e=>setF({...f,city:e.target.value})} className="w-full h-10 rounded-md border border-input px-3 text-sm">
              <option value="">Selecione a cidade</option>
              {CITIES.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <Input data-testid="cad-address" placeholder="Endereço" value={f.address} onChange={e=>setF({...f,address:e.target.value})}/>
          </>)}
          {step===3 && (<>
            <Input data-testid="cad-password" type="password" placeholder="Crie uma senha" value={f.password} onChange={e=>setF({...f,password:e.target.value})}/>
            <p className="text-xs text-slate-500">Use ao menos 8 caracteres, com letras e números.</p>
          </>)}
          {step===4 && (<>
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700">
              Ao criar sua conta, você concorda com nossos <Link to="/termos" className="text-[#0D7C66] underline">Termos de Uso</Link> e <Link to="/privacidade" className="text-[#0D7C66] underline">Política de Privacidade</Link>, incluindo o tratamento de dados conforme a LGPD.
            </div>
            <label className="flex gap-2 items-start text-sm text-slate-600">
              <Checkbox data-testid="cad-terms" checked={terms} onCheckedChange={v=>setTerms(!!v)}/>
              <span>Li e concordo com os termos e a política de privacidade.</span>
            </label>
          </>)}
          {step===5 && (<div className="p-6 rounded-2xl bg-[#FEF8F3] border border-[#FF7D54]/20">
            <div className="text-sm text-slate-600">Confirme os dados e crie sua conta. Você poderá escolher seu plano na próxima tela do seu painel.</div>
            <div className="mt-4 text-sm space-y-1"><div><b>Nome:</b> {f.name}</div><div><b>E-mail:</b> {f.email}</div><div><b>Perfil:</b> {f.role}</div><div><b>Cidade:</b> {f.city}</div></div>
          </div>)}

          <div className="flex justify-between mt-6">
            {step>1 ? <Button data-testid="cad-back" variant="outline" onClick={back}>Voltar</Button> : <div/>}
            {step<5 ? <Button data-testid="cad-next" onClick={next} className="bg-[#0D7C66] hover:bg-[#074E41]">Continuar</Button>
                    : <Button data-testid="cad-submit" onClick={submit} disabled={loading} className="bg-[#FF7D54] hover:bg-[#e56a44]">{loading?"Criando...":"Criar conta"}</Button>}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
